/**
 * Метрики шрифта и вместимость текстового слота (01-SPEC.md §3.10.6).
 *
 * Вместимость **считается из геометрии слота и метрик шрифта**, а измерение
 * по 26 текстовым блокам свёрстанной книги (`docs/04-PROMPT-FIXTURES.md` §8) —
 * калибровка формулы, а не константа для подстановки.
 *
 * Откуда взяты метрики Playpen Sans: средняя ширина знака измерена по таблицам
 * `hmtx`/`cmap` шрифта `@fontsource/playpen-sans` (начертание 500, подмножества
 * `cyrillic` + `latin`) на тексте всех 26 блоков книги — 0.5617 em на символ,
 * с учётом пробелов. Межстрочные величины взяты из компонента `LineHighlightText`
 * (`line-height: 1.55`, зазор между строками `0.35em`), который и рисует
 * отдельную плашку под каждой строкой.
 */
import { ptToMm } from './units.js';
/** Метрики по умолчанию — Playpen Sans, шрифт книги из `max-book`. */
export const DEFAULT_FONT_METRICS = Object.freeze({
    avgCharAdvanceEm: 0.562,
    lineHeightEm: 1.55,
    lineGapEm: 0.35,
});
export const FONT_METRICS = Object.freeze({
    'Playpen Sans': DEFAULT_FONT_METRICS,
});
/** Метрики шрифта по его имени; для неизвестного шрифта — метрики по умолчанию. */
export function fontMetricsFor(fontFamily) {
    return FONT_METRICS[fontFamily] ?? DEFAULT_FONT_METRICS;
}
/** Шаг строки (базовая линия к базовой линии) в миллиметрах. */
export function lineAdvanceMm(metrics, sizePt) {
    return (metrics.lineHeightEm + metrics.lineGapEm) * ptToMm(sizePt);
}
/**
 * Вместимость текста в слоте заданной геометрии.
 *
 * @param slot полезная площадь слота (безопасные поля уже вычтены)
 * @param maxCharsPerLineCap типографский предел длины строки по читаемости;
 *        нужен раскладке `band`, где полоса шириной в разворот даёт нечитаемо
 *        длинные строки (01-SPEC.md §3.10.6).
 */
export function capacityForSlot(slot, metrics, sizePt, maxCharsPerLineCap) {
    const sizeMm = ptToMm(sizePt);
    const charMm = metrics.avgCharAdvanceEm * sizeMm;
    const geometricChars = Math.max(1, Math.floor(slot.widthMm / charMm));
    const maxCharsPerLine = maxCharsPerLineCap === undefined
        ? geometricChars
        : Math.min(geometricChars, maxCharsPerLineCap);
    const maxLines = Math.max(1, Math.floor(slot.heightMm / lineAdvanceMm(metrics, sizePt)));
    return { maxCharsPerLine, maxLines };
}
/**
 * Ширина знаков, которых нет в Playpen Sans.
 *
 * Бюджет строки считается в знаках Playpen Sans средней ширины
 * (`avgCharAdvanceEm`), а грузинский и корейский текст браузер и PDF рисуют
 * запасными шрифтами — Noto Sans Georgian и Noto Sans KR. Их знаки шире:
 * в среднем 0.615 em у грузинского и 0.92 em у слога хангыля против 0.562.
 * Считать такой текст по `.length` значит получать строки, которые не влезают
 * в собственную плашку.
 *
 * Значения сняты из таблиц `hmtx`/`cmap` начертания 500 пакетов
 * `@fontsource/noto-sans-georgian` и `@fontsource/noto-sans-kr` 5.3.0.
 * У грузинских букв разброс от 0.36 до 1.1 em, поэтому для мхедрули — точные
 * ширины по буквам, а не средняя: иначе длинные слова из широких букв
 * всё равно вылезали бы.
 */
const GEORGIAN_ADVANCE_EM = Object.freeze({
    ა: 0.56,
    ბ: 0.611,
    გ: 0.601,
    დ: 0.892,
    ე: 0.594,
    ვ: 0.589,
    ზ: 0.629,
    თ: 0.891,
    ი: 0.583,
    კ: 0.574,
    ლ: 1.105,
    მ: 0.61,
    ნ: 0.61,
    ო: 0.82,
    პ: 0.584,
    ჟ: 0.628,
    რ: 0.819,
    ს: 0.613,
    ტ: 0.772,
    უ: 0.614,
    ფ: 0.87,
    ქ: 0.594,
    ღ: 0.82,
    ყ: 0.603,
    შ: 0.61,
    ჩ: 0.602,
    ც: 0.674,
    ძ: 0.61,
    წ: 0.61,
    ჭ: 0.599,
    ხ: 0.61,
    ჯ: 0.574,
    ჰ: 0.574,
    ჱ: 0.679,
    ჲ: 0.593,
    ჳ: 0.589,
    ჴ: 0.579,
    ჵ: 0.66,
    ჶ: 0.929,
    ჷ: 0.564,
    ჸ: 0.603,
    ჹ: 0.611,
    ჺ: 0.492,
    '჻': 0.54,
    ჼ: 0.359,
    ჽ: 0.564,
    ჾ: 0.593,
    ჿ: 0.611,
});
/** Прочая грузинская письменность (мтаврули, асомтаврули) — по самому широкому знаку. */
const GEORGIAN_OTHER_ADVANCE_EM = 1.158;
/** Слоги и совместимые чамо хангыля в Noto Sans KR одной ширины. */
const HANGUL_ADVANCE_EM = 0.92;
/** Иероглифы, CJK-пунктуация и полноширинные формы — ровно кегль. */
const CJK_ADVANCE_EM = 1;
function fallbackAdvanceEm(char) {
    const georgian = GEORGIAN_ADVANCE_EM[char];
    if (georgian !== undefined)
        return georgian;
    const code = char.codePointAt(0) ?? 0;
    if ((code >= 0x10a0 && code <= 0x10ff) ||
        (code >= 0x1c90 && code <= 0x1cbf) ||
        (code >= 0x2d00 && code <= 0x2d2f)) {
        return GEORGIAN_OTHER_ADVANCE_EM;
    }
    if ((code >= 0xac00 && code <= 0xd7a3) ||
        (code >= 0x1100 && code <= 0x11ff) ||
        (code >= 0x3130 && code <= 0x318f)) {
        return HANGUL_ADVANCE_EM;
    }
    if ((code >= 0x3000 && code <= 0x303f) ||
        (code >= 0x4e00 && code <= 0x9fff) ||
        (code >= 0xff00 && code <= 0xff60)) {
        return CJK_ADVANCE_EM;
    }
    return null;
}
/**
 * Ширина текста в знаках Playpen Sans средней ширины — в тех же единицах, что
 * `maxCharsPerLine`. Для кириллицы и латиницы совпадает с числом знаков.
 */
export function textWidthChars(text) {
    let width = 0;
    for (const char of text) {
        const advance = fallbackAdvanceEm(char);
        width += advance === null ? 1 : advance / DEFAULT_FONT_METRICS.avgCharAdvanceEm;
    }
    return width;
}
/** Минимум слов в строке — короче считается «висячей» (01-SPEC.md §3.10.6). */
export const MIN_WORDS_PER_LINE = 3;
/** Знаки, после которых перенос строки читается естественно. */
const BREAK_AFTER = /[.!?…,;:—–)»"']$/u;
/** Союзы и частицы, которыми плохо заканчивать строку. */
const WEAK_LINE_ENDINGS = new Set([
    'и',
    'а',
    'но',
    'да',
    'или',
    'что',
    'чтобы',
    'как',
    'же',
    'бы',
    'ли',
    'в',
    'во',
    'на',
    'к',
    'ко',
    'с',
    'со',
    'у',
    'о',
    'об',
    'от',
    'до',
    'за',
    'по',
    'из',
    'под',
    'над',
    'для',
    'при',
    'не',
    'ни',
]);
function words(line) {
    return line.split(/\s+/u).filter((w) => w.length > 0);
}
function joinWords(list) {
    return list.join(' ');
}
function endsWeakly(line) {
    const parts = words(line);
    const last = parts[parts.length - 1];
    if (last === undefined)
        return false;
    const bare = last.replace(/[^\p{L}\p{N}]/gu, '').toLowerCase();
    return bare.length > 0 && WEAK_LINE_ENDINGS.has(bare);
}
/**
 * Перенос одного абзаца по смысловым границам.
 * Слова не режутся никогда; слово длиннее строки занимает строку целиком.
 */
function wrapParagraph(paragraph, maxCharsPerLine) {
    const source = words(paragraph);
    if (source.length === 0)
        return [''];
    const lines = [];
    let current = [];
    for (const word of source) {
        const candidate = current.length === 0 ? word : `${joinWords(current)} ${word}`;
        if (textWidthChars(candidate) <= maxCharsPerLine || current.length === 0) {
            current.push(word);
        }
        else {
            lines.push(current);
            current = [word];
        }
    }
    if (current.length > 0)
        lines.push(current);
    preferSemanticBreaks(lines, maxCharsPerLine);
    avoidHangingLastLine(lines, maxCharsPerLine);
    return lines.map(joinWords);
}
/**
 * Сдвигает границы строк так, чтобы строка по возможности заканчивалась знаком
 * препинания и не заканчивалась союзом. Строка не укорачивается больше чем
 * на треть — иначе блок перестаёт быть соразмерным.
 */
function preferSemanticBreaks(lines, maxCharsPerLine) {
    const minKeep = Math.floor(maxCharsPerLine * 0.66);
    for (let i = 0; i < lines.length - 1; i += 1) {
        const line = lines[i];
        const next = lines[i + 1];
        if (!line || !next)
            continue;
        const text = joinWords(line);
        if (BREAK_AFTER.test(text) && !endsWeakly(text))
            continue;
        const weakEnding = endsWeakly(text);
        for (let take = 1; take <= 2 && line.length - take >= 1; take += 1) {
            const head = line.slice(0, line.length - take);
            const moved = line.slice(line.length - take);
            const headText = joinWords(head);
            // Висящий союз хуже немного более короткой строки, поэтому для него
            // ограничение в 66% не применяем.
            if (textWidthChars(headText) < minKeep && !weakEnding)
                break;
            const nextText = joinWords([...moved, ...next]);
            if (textWidthChars(nextText) > maxCharsPerLine)
                continue;
            if (endsWeakly(headText))
                continue;
            if (!weakEnding && !BREAK_AFTER.test(headText))
                continue;
            lines[i] = head;
            lines[i + 1] = [...moved, ...next];
            break;
        }
    }
}
/** Не оставляет последнюю строку короче `MIN_WORDS_PER_LINE` слов. */
function avoidHangingLastLine(lines, maxCharsPerLine) {
    if (lines.length < 2)
        return;
    let guard = 0;
    for (;;) {
        guard += 1;
        if (guard > maxCharsPerLine)
            return;
        const last = lines[lines.length - 1];
        const prev = lines[lines.length - 2];
        if (!last || !prev)
            return;
        // Подтягивая слова в короткую последнюю строку, не оставляем на предыдущей
        // висящий союз или предлог: переносим его вместе со следующей фразой.
        if (last.length >= MIN_WORDS_PER_LINE && !endsWeakly(joinWords(prev)))
            return;
        if (prev.length <= MIN_WORDS_PER_LINE)
            return;
        const moved = prev[prev.length - 1];
        if (moved === undefined)
            return;
        const candidate = [moved, ...last];
        if (textWidthChars(joinWords(candidate)) > maxCharsPerLine)
            return;
        lines[lines.length - 2] = prev.slice(0, prev.length - 1);
        lines[lines.length - 1] = candidate;
    }
}
/**
 * Разбивает текст на строки. Уже расставленные пользователем `\n` — жёсткие
 * переносы: они сохраняются, а слишком длинные строки дополнительно переносятся.
 */
export function wrapText(text, maxCharsPerLine) {
    const paragraphs = text.split('\n');
    const result = [];
    for (const paragraph of paragraphs) {
        if (paragraph.trim().length === 0) {
            result.push('');
            continue;
        }
        result.push(...wrapParagraph(paragraph, maxCharsPerLine));
    }
    return result;
}
/** Померить, помещается ли текст в слот заданной вместимости. */
export function measureFit(text, capacity) {
    const lines = wrapText(text, capacity.maxCharsPerLine);
    const overflowLines = lines.slice(capacity.maxLines);
    const overflowChars = overflowLines.reduce((sum, line) => sum + line.length, 0);
    return {
        fits: lines.length <= capacity.maxLines,
        lines,
        capacity,
        overflowChars,
    };
}
//# sourceMappingURL=text-metrics.js.map